import { Button, Card, Spin, Tag, Typography } from "antd";
import { useGetPartnerQuery } from "api/Partner/hooks";
import { Drawer } from "components/UI/Drawer";
import { DrawerContent } from "components/UI/DrawerContent/DrawerContent";
import { DrawerTitle } from "components/UI/DrawerTitle/DrawerTitle";
import { useDrawer } from "hooks/useDrawer";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useUser } from "hooks/useUser";
import { FC } from "react";
import { UserWebTypeEnum } from "types/enums";
import { DrawerId } from "types/models";
import { getDrawerSearchParams } from "utils/drawerHelpers";

interface PartnerDetailDrawerProps {
    partnerId: string;
}

export const PartnerDetailDrawer: FC<PartnerDetailDrawerProps> = ({ partnerId }) => {
    const { t } = usePaldockTranslation("drawers.partner-detail");
    const { t: tCategory } = usePaldockTranslation("general.tenant_category");
    const { t: tWebType } = usePaldockTranslation("general.tenant_web_type");
    const id = DrawerId.PartnerDetail;
    const drawerUtils = useDrawer();
    const { getAuthenticatedUser } = useUser();

    const { data, isLoading } = useGetPartnerQuery(partnerId, { enabled: !!partnerId });
    const { partner } = data || {};
    const currentUserId = getAuthenticatedUser()?.id;

    const handleInvite = () => {
        drawerUtils.show(DrawerId.InvitePartner, { "invitePartner-partnerId": partnerId });
    };

    const extraButtons =
        partnerId !== currentUserId ? (
            <Button size={"large"} type={"primary"} onClick={handleInvite}>
                {t("invite")}
            </Button>
        ) : undefined;

    return (
        <Drawer
            drawerId={id}
            title={<DrawerTitle mainTitle={partner?.nickname ?? t("title")} />}
            searchParamsToClean={getDrawerSearchParams(id, "partnerId")}
            extraButtons={extraButtons}
        >
            <DrawerContent>
                {isLoading ? (
                    <div className="flex justify-center items-center p-8">
                        <Spin />
                    </div>
                ) : (
                    <div className="flex flex-col gap-3 p-4">
                        <Card size="small" title={t("further_info")}>
                            {partner?.affiliate_propagation_info ? (
                                <Typography.Paragraph>{partner.affiliate_propagation_info}</Typography.Paragraph>
                            ) : (
                                <Typography.Text type="secondary">{t("no_info")}</Typography.Text>
                            )}
                        </Card>

                        {partner?.catalogCategories && partner.catalogCategories.length > 0 && (
                            <Card size="small" title={t("categories")}>
                                <div className="flex flex-wrap gap-1">
                                    {partner.catalogCategories.map((cc) => (
                                        <Tag key={cc.id}>{tCategory(cc.category)}</Tag>
                                    ))}
                                </div>
                            </Card>
                        )}

                        {partner?.catalogWebTypes && partner.catalogWebTypes.length > 0 && (
                            <Card size="small" title={t("web_types")}>
                                <div className="flex flex-wrap gap-1">
                                    {partner.catalogWebTypes.map((wt) => (
                                        <Tag key={wt.id}>{tWebType(wt.web_type as UserWebTypeEnum)}</Tag>
                                    ))}
                                </div>
                            </Card>
                        )}

                        {partner?.websites && partner.websites.length > 0 && (
                            <Card size="small" title={t("websites")}>
                                <div className="flex flex-col gap-1">
                                    {partner.websites.map((site) => {
                                        let display: string;
                                        try {
                                            display = new URL(site.url).hostname;
                                        } catch {
                                            display = site.url;
                                        }
                                        return (
                                            <a
                                                key={site.id}
                                                href={site.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {display}
                                            </a>
                                        );
                                    })}
                                </div>
                            </Card>
                        )}

                    </div>
                )}
            </DrawerContent>
        </Drawer>
    );
};
